import { useTranslation } from 'react-i18next';
import { PercentageBar } from 'qampo-ui-library';
import CircleCheckIcon from '../../assets/icons/circle-check.svg?react';
import type { SimulationStatus } from '../../hooks/useYearPlanSimulation';
import type { SimulationProgress as SimulationProgressSummary } from '../../services/simulationProgressService';
import type { YearPlanSimulationResult } from '../../services/yearPlanSimulationService';
import { formatDkk } from '../../utils/currencyUtils';
import { SimulationProgressTable } from './SimulationProgressTable';
import styles from './YearPlanSimulationModal.module.css';

interface SimulationProgressProps {
  status: SimulationStatus;
  progress: SimulationProgressSummary;
  result: YearPlanSimulationResult | null;
  professionName: (professionId: string) => string;
}

const STATUS_KEY: Record<SimulationStatus, string> = {
  idle: '',
  running: 'simulation-progress-running',
  done: 'simulation-progress-done',
  failed: 'simulation-status-failed'
};

const PERCENT = 100;

export const SimulationProgress = ({
  status,
  progress,
  result,
  professionName
}: SimulationProgressProps) => {
  const { t, i18n } = useTranslation();
  const isRunning = status === 'running';
  const isDone = status === 'done';
  const progressPercent =
    progress.requestedCourseCount > 0
      ? (progress.processedCourseCount / progress.requestedCourseCount) * PERCENT
      : 0;

  return (
    <div className={styles.body}>
      {/* Busy while the counts tick, so a screen reader announces where the
          run ended rather than every course on the way. */}
      <output className={styles.status} aria-busy={isRunning}>
        {isDone && <CircleCheckIcon className={styles.statusIcon} />}
        <span>
          {t(STATUS_KEY[status], {
            count: progress.requestedCourseCount,
            processed: progress.processedCourseCount,
            placed: progress.placedCourseCount
          })}
        </span>
      </output>
      {isRunning && (
        <PercentageBar
          value={progressPercent}
          hidePercentageValue
          small
          // The library paints the filled part with the track colour and the
          // rest with the bar colour, the reverse of what the names suggest.
          percentageBarColor="var(--color-surface-tertiary)"
          percentageTrackColor="var(--color-interaction-main)"
          styles={{ root: { width: '100%' }, barContainer: { width: '100%' } }}
        />
      )}
      <SimulationProgressTable
        rows={progress.rows}
        isDone={isDone}
        professionName={professionName}
      />
      <p className={styles.totals}>
        {t('simulation-progress-totals', {
          count: progress.requestedCourseCount,
          placed: progress.placedCourseCount,
          patients: progress.placedPatientCount,
          revenue: formatDkk(progress.placedRevenue, i18n.language)
        })}
      </p>
      {result && (
        <p className={styles.hint}>
          {result.plans.length > 0 ? t('simulation-apply-hint') : t('simulation-nothing-placed')}
        </p>
      )}
    </div>
  );
};
